export type InvoiceStatus = 'draft' | 'sent' | 'partial' | 'paid' | 'overdue';

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface InvoiceRecord {
  id: number;
  invoice_number: string;
  quote_submission_id?: number | null;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  billing_street_address?: string | null;
  billing_city?: string | null;
  billing_province?: string | null;
  billing_postal_code?: string | null;
  source?: string | null;
  issue_date: string;
  due_date?: string | null;
  line_items: InvoiceLineItem[] | unknown;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  status: InvoiceStatus | string;
  notes?: string | null;
  deposit_rate?: number | null;
  delivery_rate?: number | null;
  final_inspection_rate?: number | null;
  created_at: string;
}

export interface InvoicePaymentRates {
  depositRate: number;
  deliveryRate: number;
  finalInspectionRate: number;
}

export interface InvoicePayment {
  id: number;
  invoice_id: number;
  payment_date: string;
  amount: number;
  method: string;
  reference?: string | null;
  notes?: string | null;
  created_at?: string;
}

export interface QuoteInvoiceSource {
  id: number;
  name?: string;
  email?: string;
  phone?: string;
  street_address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  source?: string;
  pergola_model?: string;
  pergola_size?: string;
  selected_addons?: string[];
  base_price?: number;
  addons_total?: number;
  install_cost?: number;
  delivery_fee?: number;
  fall_sale_discount?: number;
  subtotal?: number;
  hst?: number;
  total_with_hst?: number;
}

export interface InvoiceDraft {
  invoice_number: string;
  quote_submission_id: number | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  billing_street_address: string;
  billing_city: string;
  billing_province: string;
  billing_postal_code: string;
  source: string;
  issue_date: string;
  due_date: string;
  line_items: InvoiceLineItem[];
  tax_rate: number;
  notes: string;
  status: InvoiceStatus;
  payment_rates: InvoicePaymentRates;
}

const DEFAULT_TAX_RATE = 0.13;
const DEFAULT_STARTING_SEQUENCE = 1001;

/**
 * Split applied to invoices issued from 2026-08 onward. Stored on each invoice
 * row at save time so changing this never rewrites already-issued invoices.
 */
export const INVOICE_PAYMENT_SCHEDULE: InvoicePaymentRates = {
  depositRate: 0.4,
  deliveryRate: 0.4,
  finalInspectionRate: 0.2,
};

/** Split used before per-invoice rates were stored (EVIE-1001, EVIE-1002). */
export const LEGACY_INVOICE_PAYMENT_SCHEDULE: InvoicePaymentRates = {
  depositRate: 0.3,
  deliveryRate: 0.6,
  finalInspectionRate: 0.1,
};

export const formatSchedulePercent = (rate: number): string => `${Math.round(rate * 100)}%`;

/**
 * Rates an invoice was issued under. Rows saved before the schedule columns
 * existed have no stored rates, so they keep the legacy 30/60/10 split.
 */
export const getInvoicePaymentRates = (
  invoice: Pick<InvoiceRecord, 'deposit_rate' | 'delivery_rate' | 'final_inspection_rate'>,
): InvoicePaymentRates => {
  const rates = [invoice.deposit_rate, invoice.delivery_rate, invoice.final_inspection_rate];
  const hasStoredRates = rates.every((rate) => rate !== null && rate !== undefined && Number.isFinite(Number(rate)));

  if (!hasStoredRates) {
    return LEGACY_INVOICE_PAYMENT_SCHEDULE;
  }

  return {
    depositRate: toNumber(invoice.deposit_rate),
    deliveryRate: toNumber(invoice.delivery_rate),
    finalInspectionRate: toNumber(invoice.final_inspection_rate),
  };
};

export const buildScheduleLabels = (rates: InvoicePaymentRates) => ({
  deposit: formatSchedulePercent(rates.depositRate),
  delivery: formatSchedulePercent(rates.deliveryRate),
  finalInspection: formatSchedulePercent(rates.finalInspectionRate),
});

const EVIE_COMPANY_INFO = {
  address: '10 Glenmore Rd, Toronto, ON, M4L3L9',
  phone: '416-435-1855',
  hstNumber: '822246930RT0001',
} as const;

const roundCurrency = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const clampCurrency = (value: number): number => Math.max(0, roundCurrency(value));

const toNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDateInput = (date: Date): string => date.toISOString().split('T')[0];

const addDays = (dateString: string, days: number): string => {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toDateInput(date);
};

const todayInput = (): string => toDateInput(new Date());

const escapeHtml = (value?: string | null): string =>
  (value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatPrintCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);

export const emptyInvoiceLineItem = (): InvoiceLineItem => ({
  description: '',
  quantity: 1,
  unitPrice: 0,
  total: 0,
});

export const calculateLineItemTotal = (quantity: number, unitPrice: number): number =>
  roundCurrency(toNumber(quantity) * toNumber(unitPrice));

export const normalizeLineItem = (item: Partial<InvoiceLineItem>): InvoiceLineItem => {
  const quantity = Math.max(0, toNumber(item.quantity || 0));
  const unitPrice = roundCurrency(toNumber(item.unitPrice || 0));
  return {
    description: item.description?.trim() || '',
    quantity,
    unitPrice,
    total: calculateLineItemTotal(quantity, unitPrice),
  };
};

export const normalizeInvoiceLineItems = (lineItems: unknown): InvoiceLineItem[] => {
  if (!Array.isArray(lineItems)) {
    return [];
  }

  return lineItems
    .map((item) => normalizeLineItem((item || {}) as Partial<InvoiceLineItem>))
    .filter((item) => item.description || item.total !== 0 || item.quantity !== 0);
};

export const calculateInvoiceTotals = (
  lineItems: InvoiceLineItem[],
  taxRate = DEFAULT_TAX_RATE,
  payments: Pick<InvoicePayment, 'amount'>[] = [],
) => {
  const subtotal = roundCurrency(lineItems.reduce((sum, item) => sum + calculateLineItemTotal(item.quantity, item.unitPrice), 0));
  const taxAmount = roundCurrency(subtotal * Math.max(0, taxRate));
  const totalAmount = roundCurrency(subtotal + taxAmount);
  const amountPaid = roundCurrency(payments.reduce((sum, payment) => sum + toNumber(payment.amount), 0));
  const balanceDue = clampCurrency(totalAmount - amountPaid);

  return {
    subtotal,
    taxAmount,
    totalAmount,
    amountPaid,
    balanceDue,
  };
};

export const calculateInvoicePaymentSchedule = (
  totalAmount: number,
  amountPaid = 0,
  rates: InvoicePaymentRates = INVOICE_PAYMENT_SCHEDULE,
) => {
  const normalizedTotal = roundCurrency(Math.max(0, toNumber(totalAmount)));
  const normalizedPaid = roundCurrency(Math.max(0, toNumber(amountPaid)));
  const depositDue = roundCurrency(normalizedTotal * rates.depositRate);
  const deliveryDue = roundCurrency(normalizedTotal * rates.deliveryRate);
  const finalInspectionDue = roundCurrency(normalizedTotal * rates.finalInspectionRate);
  const amountDueToday = clampCurrency(depositDue - normalizedPaid);
  const remainingBalance = clampCurrency(normalizedTotal - normalizedPaid);

  return {
    depositDue,
    deliveryDue,
    finalInspectionDue,
    amountDueToday,
    remainingBalance,
  };
};

export const deriveInvoiceStatus = (invoice: Pick<InvoiceRecord, 'status' | 'due_date' | 'total_amount' | 'amount_paid' | 'balance_due'>): InvoiceStatus => {
  const totalAmount = toNumber(invoice.total_amount);
  const amountPaid = toNumber(invoice.amount_paid);
  const balanceDue = typeof invoice.balance_due === 'number'
    ? toNumber(invoice.balance_due)
    : clampCurrency(totalAmount - amountPaid);

  if (totalAmount > 0 && balanceDue <= 0) {
    return 'paid';
  }

  if (amountPaid > 0) {
    return 'partial';
  }

  if (invoice.status === 'draft') {
    return 'draft';
  }

  if (invoice.due_date) {
    const dueDate = new Date(`${invoice.due_date}T23:59:59`);
    if (!Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now()) {
      return 'overdue';
    }
  }

  return 'sent';
};

export const createNextInvoiceNumber = (existingNumbers: string[], prefix = 'EVIE-'): string => {
  const sequence = existingNumbers.reduce((max, current) => {
    const match = current.match(new RegExp(`^${prefix.replace('-', '\\-')}(\\d+)$`));
    if (!match) {
      return max;
    }
    return Math.max(max, Number(match[1]));
  }, DEFAULT_STARTING_SEQUENCE - 1);

  return `${prefix}${sequence + 1}`;
};

export const buildManualInvoiceDraft = (existingNumbers: string[]): InvoiceDraft => {
  const issueDate = todayInput();
  return {
    invoice_number: createNextInvoiceNumber(existingNumbers),
    quote_submission_id: null,
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    billing_street_address: '',
    billing_city: '',
    billing_province: '',
    billing_postal_code: '',
    source: 'manual',
    issue_date: issueDate,
    due_date: addDays(issueDate, 7),
    line_items: [emptyInvoiceLineItem()],
    tax_rate: DEFAULT_TAX_RATE,
    notes: '',
    status: 'draft',
    payment_rates: INVOICE_PAYMENT_SCHEDULE,
  };
};

export const buildInvoiceDraftFromQuote = (quote: QuoteInvoiceSource, existingNumbers: string[]): InvoiceDraft => {
  const issueDate = todayInput();
  const items: InvoiceLineItem[] = [];
  const productName = quote.source?.includes('awning') ? 'Awning' : 'Pergola';
  const model = [quote.pergola_model, quote.pergola_size].filter(Boolean).join(' ').trim();
  const descriptor = model || productName;

  if (toNumber(quote.base_price) > 0) {
    items.push(normalizeLineItem({ description: `${descriptor} base price`, quantity: 1, unitPrice: toNumber(quote.base_price) }));
  }
  if (toNumber(quote.addons_total) > 0) {
    items.push(normalizeLineItem({ description: 'Add-ons', quantity: 1, unitPrice: toNumber(quote.addons_total) }));
  }
  if (toNumber(quote.install_cost) > 0) {
    items.push(normalizeLineItem({ description: 'Installation', quantity: 1, unitPrice: toNumber(quote.install_cost) }));
  }
  if (toNumber(quote.delivery_fee) > 0) {
    items.push(normalizeLineItem({ description: 'Delivery', quantity: 1, unitPrice: toNumber(quote.delivery_fee) }));
  }
  if (toNumber(quote.fall_sale_discount) > 0) {
    items.push(normalizeLineItem({ description: 'Discount', quantity: 1, unitPrice: -toNumber(quote.fall_sale_discount) }));
  }
  if (items.length === 0 && toNumber(quote.total_with_hst) > 0) {
    const inferredSubtotal = toNumber(quote.subtotal) || roundCurrency(toNumber(quote.total_with_hst) / (1 + DEFAULT_TAX_RATE));
    items.push(normalizeLineItem({ description: descriptor || 'Outdoor living project', quantity: 1, unitPrice: inferredSubtotal }));
  }

  const taxRate = toNumber(quote.subtotal) > 0 && toNumber(quote.hst) > 0
    ? roundCurrency(toNumber(quote.hst) / toNumber(quote.subtotal))
    : DEFAULT_TAX_RATE;

  return {
    invoice_number: createNextInvoiceNumber(existingNumbers),
    quote_submission_id: quote.id,
    customer_name: quote.name || '',
    customer_email: quote.email || '',
    customer_phone: quote.phone || '',
    billing_street_address: quote.street_address || '',
    billing_city: quote.city || '',
    billing_province: quote.province || '',
    billing_postal_code: quote.postal_code || '',
    source: quote.source || 'quote_submission',
    issue_date: issueDate,
    due_date: addDays(issueDate, 7),
    line_items: items.length > 0 ? items : [emptyInvoiceLineItem()],
    tax_rate: taxRate,
    notes: `Created from quote submission #${quote.id}.`,
    status: 'draft',
    payment_rates: INVOICE_PAYMENT_SCHEDULE,
  };
};

export const buildInvoiceDraftFromInvoice = (invoice: InvoiceRecord): InvoiceDraft => ({
  invoice_number: invoice.invoice_number || '',
  quote_submission_id: invoice.quote_submission_id || null,
  customer_name: invoice.customer_name || '',
  customer_email: invoice.customer_email || '',
  customer_phone: invoice.customer_phone || '',
  billing_street_address: invoice.billing_street_address || '',
  billing_city: invoice.billing_city || '',
  billing_province: invoice.billing_province || '',
  billing_postal_code: invoice.billing_postal_code || '',
  source: invoice.source || 'manual',
  issue_date: invoice.issue_date || todayInput(),
  due_date: invoice.due_date || '',
  line_items: normalizeInvoiceLineItems(invoice.line_items).length > 0
    ? normalizeInvoiceLineItems(invoice.line_items)
    : [emptyInvoiceLineItem()],
  tax_rate: toNumber(invoice.tax_rate) || DEFAULT_TAX_RATE,
  notes: invoice.notes || '',
  status: invoice.status === 'draft' ? 'draft' : 'sent',
  // Editing an issued invoice keeps the split it was issued under.
  payment_rates: getInvoicePaymentRates(invoice),
});

export const buildInvoiceDraftFromExistingInvoice = (invoice: InvoiceRecord, existingNumbers: string[]): InvoiceDraft => {
  const issueDate = todayInput();
  const lineItems = normalizeInvoiceLineItems(invoice.line_items);

  return {
    invoice_number: createNextInvoiceNumber(existingNumbers),
    quote_submission_id: null,
    customer_name: invoice.customer_name || '',
    customer_email: invoice.customer_email || '',
    customer_phone: invoice.customer_phone || '',
    billing_street_address: invoice.billing_street_address || '',
    billing_city: invoice.billing_city || '',
    billing_province: invoice.billing_province || '',
    billing_postal_code: invoice.billing_postal_code || '',
    source: invoice.source || 'manual',
    issue_date: issueDate,
    due_date: addDays(issueDate, 7),
    line_items: lineItems.length > 0 ? lineItems : [emptyInvoiceLineItem()],
    tax_rate: toNumber(invoice.tax_rate) || DEFAULT_TAX_RATE,
    notes: '',
    status: 'draft',
    // A duplicate is a new invoice, so it takes the current split.
    payment_rates: INVOICE_PAYMENT_SCHEDULE,
  };
};

export const getInvoicePayments = (payments: InvoicePayment[], invoiceId: number): InvoicePayment[] =>
  payments
    .filter((payment) => payment.invoice_id === invoiceId)
    .sort((a, b) => b.payment_date.localeCompare(a.payment_date));

export const buildInvoicePrintHtml = (invoice: InvoiceRecord, payments: InvoicePayment[], logoUrl: string): string => {
  const lineItems = normalizeInvoiceLineItems(invoice.line_items);
  const scheduleRates = getInvoicePaymentRates(invoice);
  const scheduleLabels = buildScheduleLabels(scheduleRates);
  const paymentSchedule = calculateInvoicePaymentSchedule(invoice.total_amount, invoice.amount_paid, scheduleRates);
  const paymentRows = payments.length > 0
    ? payments.map((payment) => `
      <tr>
        <td>${escapeHtml(payment.payment_date)}</td>
        <td>${escapeHtml(payment.method)}</td>
        <td>${escapeHtml(payment.reference)}</td>
        <td style="text-align:right;">${formatPrintCurrency(payment.amount)}</td>
      </tr>`).join('')
    : '<tr><td colspan="4" style="text-align:center;color:#64748b;">No payments recorded yet.</td></tr>';

  const itemRows = lineItems.map((item) => `
    <tr>
      <td>${escapeHtml(item.description)}</td>
      <td style="text-align:right;">${item.quantity}</td>
      <td style="text-align:right;">${formatPrintCurrency(item.unitPrice)}</td>
      <td style="text-align:right;">${formatPrintCurrency(item.total)}</td>
    </tr>`).join('');

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>${escapeHtml(invoice.invoice_number)} - Evie Outdoors</title>
      <style>
        body { font-family: Arial, sans-serif; color: #0f172a; margin: 32px; }
        .header, .summary { display: flex; justify-content: space-between; gap: 24px; }
        .header img { max-width: 220px; height: auto; }
        .pill { display: inline-block; padding: 6px 10px; background: #e2e8f0; border-radius: 999px; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th, td { border-bottom: 1px solid #e2e8f0; padding: 10px 8px; font-size: 14px; vertical-align: top; }
        th { background: #f8fafc; text-align: left; }
        .totals { width: 320px; margin-left: auto; margin-top: 20px; }
        .totals div { display: flex; justify-content: space-between; padding: 6px 0; }
        .grand-total { font-size: 18px; font-weight: 700; border-top: 2px solid #0f172a; margin-top: 8px; padding-top: 10px; }
        .section { margin-top: 28px; }
        .muted { color: #64748b; }
        .company { text-align: right; }
        .company-details { margin-top: 12px; font-size: 13px; line-height: 1.5; }
        .schedule-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 16px; }
        .schedule-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; background: #f8fafc; }
        .due-today { border-color: #bfdbfe; background: #eff6ff; }
        .label { font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #64748b; }
        .amount { font-size: 22px; font-weight: 700; margin-top: 6px; }
        .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; text-align: center; }
        @media print { body { margin: 16px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <img src="${escapeHtml(logoUrl)}" alt="Evie Outdoors" />
          <p class="muted">Premium pergolas and awnings</p>
        </div>
        <div class="company">
          <div class="pill">${escapeHtml(deriveInvoiceStatus(invoice))}</div>
          <h1>Invoice ${escapeHtml(invoice.invoice_number)}</h1>
          <p><strong>Issue date:</strong> ${escapeHtml(invoice.issue_date)}</p>
          <p><strong>Due date:</strong> ${escapeHtml(invoice.due_date || '—')}</p>
          <div class="company-details muted">
            <div>${escapeHtml(EVIE_COMPANY_INFO.address)}</div>
            <div>${escapeHtml(EVIE_COMPANY_INFO.phone)}</div>
            <div><strong>HST #:</strong> ${escapeHtml(EVIE_COMPANY_INFO.hstNumber)}</div>
          </div>
        </div>
      </div>

      <div class="summary section">
        <div>
          <h3>Bill To</h3>
          <p><strong>${escapeHtml(invoice.customer_name)}</strong></p>
          <p>${escapeHtml(invoice.customer_email)}</p>
          <p>${escapeHtml(invoice.customer_phone)}</p>
          <p>${escapeHtml([invoice.billing_street_address, invoice.billing_city, invoice.billing_province, invoice.billing_postal_code].filter(Boolean).join(', '))}</p>
        </div>
        <div>
          <h3>Notes</h3>
          <p>${escapeHtml(invoice.notes || 'Thank you for choosing Evie Outdoors.')}</p>
        </div>
      </div>

      <div class="section">
        <h3>Invoice Items</h3>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th style="text-align:right;">Qty</th>
              <th style="text-align:right;">Unit Price</th>
              <th style="text-align:right;">Amount</th>
            </tr>
          </thead>
          <tbody>${itemRows}</tbody>
        </table>

        <div class="totals">
          <div><span>Subtotal</span><strong>${formatPrintCurrency(invoice.subtotal)}</strong></div>
          <div><span>HST (${Math.round(invoice.tax_rate * 100)}%)</span><strong>${formatPrintCurrency(invoice.tax_amount)}</strong></div>
          <div><span>Total Contract Value</span><strong>${formatPrintCurrency(invoice.total_amount)}</strong></div>
          <div><span>Paid to date</span><strong>${formatPrintCurrency(invoice.amount_paid)}</strong></div>
          <div class="grand-total"><span>Amount Due Today (${scheduleLabels.deposit} Deposit)</span><span>${formatPrintCurrency(paymentSchedule.amountDueToday)}</span></div>
          <div><span>Remaining Contract Balance</span><strong>${formatPrintCurrency(paymentSchedule.remainingBalance)}</strong></div>
        </div>
      </div>

      <div class="section">
        <h3>Payment Schedule</h3>
        <div class="schedule-grid">
          <div class="schedule-card due-today">
            <div class="label">Due Today</div>
            <div class="amount">${formatPrintCurrency(paymentSchedule.amountDueToday)}</div>
            <div>${scheduleLabels.deposit} deposit milestone: ${formatPrintCurrency(paymentSchedule.depositDue)}.</div>
          </div>
          <div class="schedule-card">
            <div class="label">On Delivery</div>
            <div class="amount">${formatPrintCurrency(paymentSchedule.deliveryDue)}</div>
            <div>${scheduleLabels.delivery} due when product is delivered.</div>
          </div>
          <div class="schedule-card">
            <div class="label">Final Inspection</div>
            <div class="amount">${formatPrintCurrency(paymentSchedule.finalInspectionDue)}</div>
            <div>${scheduleLabels.finalInspection} due after final inspection.</div>
          </div>
        </div>
        <div class="schedule-card" style="margin-top: 16px;">
          <div class="label">Payment Instructions</div>
          <div style="margin-top: 8px;"><strong>E-transfer:</strong> payments@evieoutdoors.com</div>
          <div class="muted" style="margin-top: 6px;">Phone: ${escapeHtml(EVIE_COMPANY_INFO.phone)}</div>
          <div class="muted" style="margin-top: 6px;">Please do not send cheques.</div>
        </div>
      </div>

      <div class="section">
        <h3>Payments Received</h3>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Method</th>
              <th>Reference</th>
              <th style="text-align:right;">Amount</th>
            </tr>
          </thead>
          <tbody>${paymentRows}</tbody>
        </table>
      </div>

      <div class="footer">
        Evie Outdoors • ${escapeHtml(EVIE_COMPANY_INFO.address)} • ${escapeHtml(EVIE_COMPANY_INFO.phone)} • HST # ${escapeHtml(EVIE_COMPANY_INFO.hstNumber)}
      </div>
    </body>
  </html>`;
};